import React, { useCallback, useEffect, useState } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import axios from "axios";
import store from "./redux/store";
import AeternaInvicta from "./components/AeternaInvicta/AeternaInvicta";
import CardRender, {
  isCardRenderMode,
} from "./components/AeternaInvicta/CardRender";
import styles from "./components/AeternaInvicta/AeternaInvicta.module.scss";
import {
  createGuestUser,
  loadSession,
  saveAccountSession,
  saveGuestSession,
} from "./utils/session";
import { getNativePlatform } from "./utils/nativeApp";
import {
  playSplashMusic,
  stopSplashMusic,
} from "./components/AeternaInvicta/aeternaAudio";

const API_BASE = process.env.REACT_APP_AETERNA_API_URL || "";

const STATIC_HTML_REDIRECTS = {
  "/privacy": "/privacy.html",
  "/privacy/": "/privacy.html",
  "/delete-account": "/delete-account.html",
  "/delete-account/": "/delete-account.html",
  "/support": "/support.html",
  "/support/": "/support.html",
};

(function redirectStaticHtmlPages() {
  if (typeof window === "undefined") return;
  const target = STATIC_HTML_REDIRECTS[window.location.pathname];
  if (!target) return;
  const search = window.location.search || "";
  window.location.replace(`${target}${search}`);
})();

function LoginScreen({ onLoggedIn }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("guest");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    if (mode === "guest") {
      const guest = createGuestUser();
      onLoggedIn(guest);
      return;
    }

    const name = username.trim();
    if (!name) {
      setError("Enter a username.");
      return;
    }
    if (/\s/.test(name)) {
      setError("Username must not contain spaces.");
      return;
    }
    if (!password) {
      setError("Enter a password.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const base = API_BASE || "";
      if (mode === "register") {
        await axios.post(`${base}/api/user/register`, {
          username: name,
          password,
          via: getNativePlatform(),
        });
        setMode("login");
        setError("Account created. Sign in to play.");
        setBusy(false);
        return;
      }
      const response = await axios.post(`${base}/api/user/login-android`, {
        username: name,
        password,
        via: getNativePlatform(),
      });
      onLoggedIn({
        id: response.data.id || 0,
        username: response.data.username || name,
        token: response.data.token || "",
        kind: "account",
      });
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data ||
        (mode === "register"
          ? "Registration failed."
          : "Invalid username or password.");
      setError(typeof message === "string" ? message : "Request failed.");
    } finally {
      setBusy(false);
    }
  }, [username, password, mode, onLoggedIn]);

  useEffect(() => {
    playSplashMusic();
    return () => {
      stopSplashMusic();
    };
  }, []);

  return (
    <div className={`${styles.canvas} ${styles.canvasSplash}`}>
      <div className={styles.splash}>
        <div className={styles.splashBrand}>
          <h1 className={styles.splashTitle}>Aeterna Invicta</h1>
          <div className={styles.splashDescription}>
            <p>
              Aeterna Invicta strives to combine the best and most interesting
              aspects of Magic the Gathering and Path of Exile into a
              collectible card game. Build Decks, discover synergies, and play
              your cards right as you try to best your opponent.
            </p>
            <p className={styles.splashTagline}>
              Become eternal and uncontested!
            </p>
          </div>
        </div>
        <p className={styles.splashHint}>
          {mode === "guest"
            ? "Jump in as a guest. A random name is chosen for you and kept for 24 hours."
            : "Account sign-in restores your profile on this device automatically next time."}
        </p>
        {mode !== "guest" ? (
          <div className={styles.splashFields}>
            <label className={styles.splashField}>
              <span>Username</span>
              <input
                className={styles.input}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </label>
            <label className={styles.splashField}>
              <span>Password</span>
              <input
                className={styles.input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={
                  mode === "register" ? "new-password" : "current-password"
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
              />
            </label>
          </div>
        ) : null}
        {error ? <p className={styles.splashError}>{error}</p> : null}
        <div className={styles.splashActions}>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className={`${styles.button} ${styles.buttonPrimary} ${styles.splashEnterButton}`}
          >
            {busy
              ? "Please wait…"
              : mode === "register"
                ? "Create account"
                : mode === "login"
                  ? "Sign in"
                  : "Play as Guest"}
          </button>
          <button
            type="button"
            onClick={() => {
              const next =
                mode === "guest"
                  ? "login"
                  : mode === "login"
                    ? "register"
                    : "guest";
              setMode(next);
              setError("");
            }}
            className={`${styles.button} ${styles.splashTutorialsButton}`}
          >
            {mode === "guest"
              ? "Use an account instead"
              : mode === "login"
                ? "Need an account? Register"
                : "Play as guest"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Shell() {
  const dispatch = useDispatch();
  const user = useSelector((storeState) => storeState.user);
  const [ready, setReady] = useState(false);

  const handleLoggedIn = useCallback(
    (payload) => {
      const kind = payload?.kind === "account" ? "account" : "guest";
      const token =
        typeof payload?.token === "string" ? payload.token.trim() : "";
      if (kind === "account" && (!token || token === "local" || token === "guest")) {
        return;
      }
      const next = {
        id: Number(payload?.id) || 0,
        username: String(payload?.username || "").trim().slice(0, 40),
        token,
        kind,
        expiresAt:
          kind === "guest" && Number(payload?.expiresAt)
            ? Number(payload.expiresAt)
            : undefined,
      };
      if (!next.username) return;
      if (kind === "account") {
        saveAccountSession(next);
      } else {
        saveGuestSession(next);
        const saved = loadSession();
        if (saved?.expiresAt) next.expiresAt = saved.expiresAt;
      }
      dispatch({ type: "SET_USER", payload: next });
    },
    [dispatch],
  );

  useEffect(() => {
    const saved = loadSession();
    if (saved) {
      dispatch({ type: "SET_USER", payload: saved });
    }
    setReady(true);
  }, [dispatch]);

  if (!ready) return null;
  if (!user?.username) {
    return <LoginScreen onLoggedIn={handleLoggedIn} />;
  }
  return <AeternaInvicta />;
}

export default function App() {
  if (isCardRenderMode()) return <CardRender />;
  return (
    <Provider store={store}>
      <Router>
        <Shell />
      </Router>
    </Provider>
  );
}
